import type { BatchScanResponse } from '@panelvault/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';

import { api } from '../../lib/api';
import { memoryStorage, ScanOutbox, type OutboxEntry, type OutboxStorage } from './outbox';

const DB_NAME = 'panelvault';
const STORE = 'scan-outbox';
/** While scans wait, try again this often (and whenever the device comes online). */
const RETRY_MS = 20_000;

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** IndexedDB survives closing the app, which is the point of the outbox. */
function indexedDbStorage(): OutboxStorage {
  const opened = new Promise<IDBDatabase>((resolve, reject) => {
    const open = indexedDB.open(DB_NAME, 1);
    open.onupgradeneeded = () => {
      open.result.createObjectStore(STORE, { keyPath: 'clientScanId' });
    };
    open.onsuccess = () => resolve(open.result);
    open.onerror = () => reject(open.error);
  });

  const store = async (mode: IDBTransactionMode) =>
    (await opened).transaction(STORE, mode).objectStore(STORE);

  return {
    load: async () => request((await store('readonly')).getAll() as IDBRequest<OutboxEntry[]>),
    save: async (entry) => {
      await request((await store('readwrite')).put(entry));
    },
    remove: async (ids) => {
      const target = await store('readwrite');
      await Promise.all(ids.map((id) => request(target.delete(id))));
    },
  };
}

function createStorage(): OutboxStorage {
  try {
    if (typeof indexedDB !== 'undefined') return indexedDbStorage();
  } catch {
    // Private mode in some browsers: fall through to memory.
  }
  return memoryStorage();
}

export const scanOutbox = new ScanOutbox(createStorage(), async (batch) => {
  const response = await api.post<BatchScanResponse>('/scan/batch', batch);
  return response.results;
});

/** The outbox entries, newest first; re-renders on every change. */
export function useScanOutbox(): readonly OutboxEntry[] {
  return useSyncExternalStore(scanOutbox.subscribe, scanOutbox.getSnapshot);
}

/**
 * Keeps the outbox moving. Mounted once in the app shell: scans made offline
 * go out as soon as the device is back online, whichever page is open.
 */
export function useOutboxSync(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const unsubscribe = scanOutbox.onSynced((result) => {
      if (result.sent === 0) return;
      for (const key of ['series', 'collection', 'stats', 'scan-queue', 'publishers']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    });

    // A failed attempt (offline, logged out) leaves the scans waiting; the
    // next one picks them up.
    const trySync = () => {
      if (scanOutbox.waitingCount > 0) void scanOutbox.sync();
    };

    void scanOutbox.init().then(trySync);
    window.addEventListener('online', trySync);
    const timer = setInterval(trySync, RETRY_MS);

    return () => {
      unsubscribe();
      window.removeEventListener('online', trySync);
      clearInterval(timer);
    };
  }, [queryClient]);
}
